"use client"

import * as React from "react"
import {
    Brain,
    Crown,
    GalleryVerticalEnd,
    HelpCircle,
    Home,
    MessageSquare,
    Monitor,
    UserCheck,
    Users,
    Zap
} from "lucide-react"

import { NavMain } from "@/salesminer/layouts/app/sidebar/nav-main.tsx"
import { NavSecondary } from "@/salesminer/layouts/app/sidebar/nav-secondary.tsx"
import { NavUser } from "@/salesminer/layouts/app/sidebar/nav-user.tsx"
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarHeader,
    SidebarMenuButton,
    SidebarRail,
} from "@/components/ui/sidebar.tsx"
import { Link, usePage } from "@inertiajs/react"
import { salesminer_app_dashboard_path, salesminer_app_faq_path, salesminer_app_intro_path } from "@/routes"

const iconMap = {
    'users': Users,
    'user-check': UserCheck,
    'monitor': Monitor,
    'crown': Crown,
    'brain': Brain,
    'zap': Zap,
}

const HomeLink = () => {
    const {props: {version, organization}} = usePage<{
        version: string,
        organization: { name: string, slug: string }
    }>()

    return (
        <SidebarMenuButton size="lg" asChild>
            <Link href={salesminer_app_dashboard_path(organization.slug)}>
                <div
                    className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                    <GalleryVerticalEnd className="size-4"/>
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-medium">SalesMiner</span>
                    <span className="">{version}</span>
                </div>
            </Link>
        </SidebarMenuButton>
    )
}

export function AppSidebar({...props}: React.ComponentProps<typeof Sidebar>) {
    const {props: {organization, user}, component} = usePage<{
        organization: { name: string, slug: string },
        user: { name: string, email: string }
    }>()

    const secondaryItems = React.useMemo(() => [
        {
            title: "Introduction",
            url: salesminer_app_intro_path(organization.slug),
            icon: HelpCircle,
            isActive: component === "App/Pages/Intro"
        },
        {
            title: "FAQ",
            url: salesminer_app_faq_path(organization.slug),
            icon: MessageSquare,
            isActive: component === "App/Pages/Faq"
        }
    ], [organization.slug, component])

    const isOverviewActive = component === "App/Dashboard/Index"

    return (
        <Sidebar collapsible="icon" {...props}>
            <SidebarHeader>
                <HomeLink/>
            </SidebarHeader>
            <SidebarContent>
                <SidebarGroup>
                    <SidebarMenuButton asChild isActive={isOverviewActive}>
                        <Link href={salesminer_app_dashboard_path(organization.slug)}>
                            <Home/>
                            <span>Overview</span>
                        </Link>
                    </SidebarMenuButton>
                </SidebarGroup>
                <NavMain/>
                <NavSecondary items={secondaryItems}/>
            </SidebarContent>
            <SidebarFooter>
                <NavUser user={user}/>
            </SidebarFooter>
            <SidebarRail/>
        </Sidebar>
    )
}
